import { useState } from "react";

export interface TopFeatureTab {
  id: string;
  label: string;
  headline: string;
  intro: string;
  items: { title: string; description: string }[];
}

interface TopFeaturesProps {
  title?: string;
  tabs: TopFeatureTab[];
}

const TopFeatures = ({ title = "Top features", tabs }: TopFeaturesProps) => {
  const [active, setActive] = useState(tabs[0]?.id);
  const current = tabs.find((tab) => tab.id === active) ?? tabs[0];

  if (!current) return null;

  return (
    <section className="py-20 md:py-28 px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <h2
          className="text-3xl md:text-4xl tracking-tight mb-8"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {title}
        </h2>

        <div
          role="tablist"
          aria-label={title}
          className="flex flex-wrap gap-2 border-b border-border pb-4 mb-10"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={tab.id === current.id}
              aria-controls={`panel-${tab.id}`}
              onClick={() => setActive(tab.id)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                tab.id === current.id
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* All panels stay in the DOM so the full feature copy is present in
            SSR HTML for crawlers; only the active one is visible. */}
        {tabs.map((tab) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`panel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            hidden={tab.id !== current.id}
            className="grid md:grid-cols-2 gap-10 md:gap-16"
          >
            <div>
              <h3
                className="text-2xl md:text-3xl tracking-tight mb-4"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {tab.headline}
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground max-w-md">
                {tab.intro}
              </p>
            </div>
            <ul className="flex flex-col gap-6">
              {tab.items.map((item) => (
                <li key={item.title}>
                  <p className="text-base font-medium text-foreground mb-1">
                    {item.title}
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default TopFeatures;
